import type { PageEntry, Entry } from 'contensis-delivery-api';
import type { PagedList } from 'contensis-core-api';

import type { ActiveFilter, Project } from 'Common/hooks/useFilters';
import { featuredResultsMapping, searchResultsMapping } from 'Lib/search/searchResultsMapping';

import type { CheckboxInputProps } from 'Elements/Input/Checkbox';

import type { Props } from './Search';

export type PageData = {
    entry: PageEntry;
    searchConfig: Entry[];
    searchResults: PagedList<Entry>;
    featuredSearchResults: PageEntry[];
    activeSearchTerm?: string;
    activeFilterQueries?: string[];
    selectedProject: Project;
};

const datamap = (data: PageData): Props => {
    const {
        entry,
        searchConfig,
        searchResults,
        featuredSearchResults,
        activeSearchTerm,
        activeFilterQueries,
        selectedProject
    } = data;

    const currentProjectFilter: Entry | undefined = searchConfig.find(
        project => project.site.apiName === selectedProject
    );

    const filtersByProject: CheckboxInputProps[] = currentProjectFilter?.filters.map(item => ({
        id: item.typeLabel,
        name: item.typeLabel,
        label: item.typeLabel,
        value: item.key
    }));

    const initialActiveFilters: ActiveFilter[] = [];
    activeFilterQueries &&
        activeFilterQueries.forEach(activeFilterItem => {
            const matchingFilters = filtersByProject
                .filter(checkboxFilterItem => checkboxFilterItem.id === activeFilterItem)
                .map(matchingFilter => ({
                    ...matchingFilter,
                    type: 'filter'
                }));

            initialActiveFilters.push(...matchingFilters);
        });

    const firstResultIndex: number =
        searchResults.pageIndex === 0
            ? 1
            : searchResults.pageIndex * searchResults.pageSize + 1 + featuredSearchResults.length;
    const lastResultIndex: number =
        searchResults.items.length < searchResults.pageSize
            ? searchResults.totalCount + featuredSearchResults.length
            : (searchResults.pageIndex + 1) * searchResults.pageSize + featuredSearchResults.length;

    return {
        heroBanner: {
            heading: entry.title,
            search: {
                id: 'search-button',
                label: 'Search',
                name: 'Search',
                placeholder: 'Search...',
                button: {
                    ariaLabel: 'Submit search'
                },
                defaultValue: activeSearchTerm
            }
        },
        tabs: searchConfig.map(project => ({
            label: project.site.label,
            activeTabId: project.site.apiName
        })),
        filters: filtersByProject,
        searchResults: {
            results: [
                ...(searchResults.pageIndex === 0
                    ? featuredResultsMapping({ results: featuredSearchResults, selectedProject })
                    : []),
                ...searchResultsMapping({ results: searchResults.items, selectedProject })
            ],
            resultsCountText: `Showing ${firstResultIndex}-${lastResultIndex} results of ${
                searchResults.totalCount + featuredSearchResults.length
            }  ${activeSearchTerm ? `for “${activeSearchTerm}”` : ''}`,
            pagination: {
                id: 'search-pagination',
                totalCount: searchResults.totalCount,
                pageSize: searchResults.pageSize,
                initialPageIndex: searchResults.pageIndex
            }
        },
        selectedProject,
        activeSearchTerm,
        initialActiveFilters
    };
};

export default datamap;
